import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, invalidatePrefix } from '../api/client';
import type { UpstreamAccount, UpstreamTestResult } from '../api/types';
import { Badge } from '../components/Badge';
import { Card, KeyValue, PageHeader } from '../components/Card';
import { ErrorBanner, LoadingBlock } from '../components/Feedback';
import { Button, Field, Input, Toggle } from '../components/Form';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { ago, dateTime, money, text } from '../lib/format';

export function UpstreamPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const path = '/admin-api/upstream/accounts';
  const query = useQuery<{ list: UpstreamAccount[] }, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get(path, signal),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
    refetchInterval: 30_000,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('https://api.cc');
  const [token, setToken] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<{ id: number; result: UpstreamTestResult } | null>(null);

  const accounts = query.data?.list ?? [];

  const openCreate = () => {
    setEditId(null);
    setName(''); setBaseUrl('https://api.cc'); setToken(''); setEnabled(true);
    setCreateOpen(true);
  };

  const openEdit = (acc: UpstreamAccount) => {
    setEditId(acc.id);
    setName(acc.name); setBaseUrl(acc.base_url); setToken(''); setEnabled(acc.enabled);
    setCreateOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editId) {
        const body: Record<string, unknown> = { name, base_url: baseUrl, enabled };
        if (token) body.token = token;
        await api.patch(`/admin-api/upstream/accounts/${editId}`, body);
        toast.success('Аккаунт обновлён');
      } else {
        await api.post('/admin-api/upstream/accounts', { name, base_url: baseUrl, token, enabled });
        toast.success('Аккаунт создан');
      }
      setCreateOpen(false);
      setToken('');
      void invalidatePrefix(queryClient, '/admin-api/upstream');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const test = async (acc: UpstreamAccount) => {
    setTestingId(acc.id);
    try {
      const result = await api.post<UpstreamTestResult>(`/admin-api/upstream/accounts/${acc.id}/test`);
      setTestResult({ id: acc.id, result });
      toast.success(`Тест ОК: ${result.username}, баланс ${money(result.balance)}`);
      void invalidatePrefix(queryClient, '/admin-api/upstream');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Тест не прошёл');
    } finally {
      setTestingId(null);
    }
  };

  const toggleEnabled = async (acc: UpstreamAccount) => {
    try {
      await api.patch(`/admin-api/upstream/accounts/${acc.id}`, { enabled: !acc.enabled });
      toast.success(acc.enabled ? 'Выключен' : 'Включён');
      void invalidatePrefix(queryClient, '/admin-api/upstream');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Upstream-аккаунты"
        description="Аккаунты upstream-провайдера. Токен не отображается полностью."
        actions={<Button variant="primary" onClick={openCreate}>Добавить аккаунт</Button>}
      />

      {query.error ? <ErrorBanner message={query.error.message} onRetry={() => query.refetch()} /> : null}

      {query.isLoading ? <LoadingBlock /> : accounts.length === 0 ? (
        <Card><div className="py-8 text-center text-sm text-zinc-500">Аккаунтов нет. Добавьте первый.</div></Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {accounts.map((acc) => (
            <Card key={acc.id} title={acc.name}>
              <KeyValue
                items={[
                  { label: 'Base URL', value: <span className="break-all text-xs">{acc.base_url}</span> },
                  { label: 'Токен', value: <span className="font-mono text-xs text-zinc-400">{acc.token_hint ?? '—'}</span> },
                  { label: 'Username', value: text(acc.username) },
                  { label: 'Баланс', value: money(acc.balance) },
                  { label: 'Проверен', value: acc.balance_checked_at ? ago(acc.balance_checked_at) : '—' },
                  { label: 'Создан', value: dateTime(acc.created_at) },
                ]}
              />
              {acc.last_error ? (
                <div className="mt-2 rounded-lg border border-red-900/60 bg-red-950/40 p-2 text-xs text-red-300">
                  <span className="font-medium text-red-200">Ошибка: </span>{acc.last_error}
                  {acc.last_error_at ? <span className="block text-[10px] text-red-400/70">{dateTime(acc.last_error_at)}</span> : null}
                </div>
              ) : null}
              {testResult?.id === acc.id ? (
                <div className="mt-2 rounded-lg border border-emerald-900/60 bg-emerald-950/40 p-2 text-xs text-emerald-200">
                  Username: {testResult.result.username} · Nickname: {text(testResult.result.nickname)} · Баланс: {money(testResult.result.balance)}
                </div>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="primary" loading={testingId === acc.id} onClick={() => test(acc)}>Тест</Button>
                <Button size="sm" variant="secondary" onClick={() => openEdit(acc)}>Изменить</Button>
                <Button size="sm" variant="ghost" onClick={() => toggleEnabled(acc)}>
                  {acc.enabled ? 'Выключить' : 'Включить'}
                </Button>
                <Badge tone={acc.enabled ? 'success' : 'muted'}>{acc.enabled ? 'вкл' : 'выкл'}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={editId ? 'Изменить аккаунт' : 'Добавить аккаунт'}
        size="md"
        footer={<><Button variant="ghost" onClick={() => setCreateOpen(false)}>Отмена</Button><Button variant="primary" loading={saving} onClick={save}>{editId ? 'Сохранить' : 'Создать'}</Button></>}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Название" className="sm:col-span-2"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Base URL" className="sm:col-span-2"><Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.cc" /></Field>
          <Field label={editId ? 'Новый токен (опционально)' : 'Токен'} className="sm:col-span-2" hint="Токен шифруется. Полностью не отображается.">
            <Input value={token} onChange={(e) => setToken(e.target.value)} type="password" autoComplete="off" placeholder={editId ? 'оставьте пустым, чтобы не менять' : 'upstream API token'} />
          </Field>
          <div className="sm:col-span-2"><Toggle checked={enabled} onChange={setEnabled} label="Включён" /></div>
        </div>
      </Modal>
    </div>
  );
}
